import httpClient from "@utils/httpClient";

// ============================================
// Types
// ============================================

export interface OrganizationDetails {
  id?: number;
  company_name: string;
  company_logo?: string;
  tagline?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  gst_number?: string;
  registration_number?: string;
  pan_number?: string;
  company_type?: string;
  industry_sector?: string;
  founded_date?: string;
  website?: string;
  email?: string;
  phone?: string;
  alternate_phone?: string;
  linkedin_url?: string;
  twitter_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface LeadershipPosition {
  id: number;
  position_code: string;
  position_title: string;
  description?: string;
  position_level: number;
  display_order: number;
  color_code: string;
  is_active: boolean;
}

export interface LeadershipAssignment {
  id: number;
  position_id: number;
  employee_id: string;
  reports_to_id?: number;
  start_date?: string;
  bio?: string;
  is_active: boolean;
  created_at: string;
  position_code: string;
  position_title: string;
  position_level: number;
  display_order: number;
  color_code: string;
  position_description?: string;
  employee_name: string;
  employee_email: string;
  employee_phone?: string;
  employee_photo?: string;
  dept_id?: number;
  department_name?: string;
  children?: LeadershipAssignment[];
}

export interface OrganizationStats {
  total_employees: number;
  total_departments: number;
  years_in_business: number;
  leadership_count: number;
  available_positions: number;
}

export interface CreateLeadershipAssignment {
  position_id: number;
  employee_id: string;
  reports_to_id?: number;
  start_date?: string;
  bio?: string;
}

export interface AvailableEmployee {
  id: string;
  name: string;
  email: string;
  contact?: string;
  profile_photo?: string;
  role_name?: string;
  department_name?: string;
}

export interface WorkingHoursSettings {
  working_hours_start: string;
  working_hours_end: string;
  working_days: string[];
  attendance_notification_enabled: boolean;
  late_punch_in_grace_minutes: number;
  missing_punch_out_grace_minutes: number;
  timezone: string;
}

export interface NotificationStatus {
  lastTriggeredAt: string | null;
  triggeredBy: string | null;
  triggeredByName: string | null;
  triggeredByEmail: string | null;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  profile_photo?: string;
  role_name?: string;
}

export interface Team {
  id: number;
  team_name: string;
  description?: string;
  manager_id?: string;
  manager_name?: string;
  manager_email?: string;
  manager_photo?: string;
  member_count: number;
  updated_at?: string;
  members?: TeamMember[];
}

export interface CreateTeam {
  team_name: string;
  description?: string;
  manager_id?: string;
}

export interface AvailableManager {
  id: string;
  name: string;
  email: string;
  profile_photo?: string;
  role_name?: string;
  current_department?: string;
  manages_team_id?: number;
  manages_team_name?: string;
}

// ============================================
// Organization Details API
// ============================================

/**
 * Get organization details
 */
export const getOrganizationDetails = async () => {
  try {
    const response = await httpClient.get('/organization-setup/details');

    return {
      success: response.success,
      data: response.data as OrganizationDetails | null,
      error: response.error
    };
  } catch (error: any) {
    console.error('getOrganizationDetails error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message }
    };
  }
};

/**
 * Update organization details (create if not exists)
 */
export const updateOrganizationDetails = async (data: Partial<OrganizationDetails>) => {
  try {
    const response = await httpClient.put('/organization-setup/details', data);

    return {
      success: response.success,
      data: response.data as OrganizationDetails,
      error: response.error,
      message: response.message
    };
  } catch (error: any) {
    console.error('updateOrganizationDetails error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to update organization details' }
    };
  }
};

// ============================================
// Leadership Positions API
// ============================================

/**
 * Get all leadership positions
 */
export const getLeadershipPositions = async () => {
  try {
    const response = await httpClient.get('/organization-setup/positions');

    return {
      success: response.success,
      data: (response.data || []) as LeadershipPosition[],
      error: response.error
    };
  } catch (error: any) {
    console.error('getLeadershipPositions error:', error);
    return {
      success: false,
      data: [],
      error: { message: error.message }
    };
  }
};

/**
 * Create a new leadership position
 */
export const createLeadershipPosition = async (data: Partial<LeadershipPosition>) => {
  try {
    const response = await httpClient.post('/organization-setup/positions', data);

    return {
      success: response.success,
      data: response.data as LeadershipPosition,
      error: response.error,
      message: response.message
    };
  } catch (error: any) {
    console.error('createLeadershipPosition error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create position' }
    };
  }
};

/**
 * Update a leadership position
 */
export const updateLeadershipPosition = async (id: number, data: Partial<LeadershipPosition>) => {
  try {
    const response = await httpClient.put(`/organization-setup/positions/${id}`, data);

    return {
      success: response.success,
      data: response.data as LeadershipPosition,
      error: response.error,
      message: response.message
    };
  } catch (error: any) {
    console.error('updateLeadershipPosition error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to update position' }
    };
  }
};

// ============================================
// Leadership Hierarchy API
// ============================================

/**
 * Get full leadership hierarchy (flat list)
 */
export const getLeadershipHierarchy = async () => {
  try {
    const response = await httpClient.get('/organization-setup/hierarchy');

    return {
      success: response.success,
      data: (response.data || []) as LeadershipAssignment[],
      error: response.error
    };
  } catch (error: any) {
    console.error('getLeadershipHierarchy error:', error);
    return {
      success: false,
      data: [],
      error: { message: error.message }
    };
  }
};

/**
 * Get leadership hierarchy as tree structure
 */
export const getLeadershipTree = async () => {
  try {
    const response = await httpClient.get('/organization-setup/hierarchy/tree');

    return {
      success: response.success,
      data: (response.data || []) as LeadershipAssignment[],
      error: response.error
    };
  } catch (error: any) {
    console.error('getLeadershipTree error:', error);
    return {
      success: false,
      data: [],
      error: { message: error.message }
    };
  }
};

/**
 * Assign employee to leadership position
 */
export const assignLeadershipPosition = async (data: CreateLeadershipAssignment) => {
  try {
    const response = await httpClient.post('/organization-setup/hierarchy', data);

    return {
      success: response.success,
      data: response.data,
      error: response.error,
      message: response.message
    };
  } catch (error: any) {
    console.error('assignLeadershipPosition error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to assign position' }
    };
  }
};

/**
 * Update leadership assignment
 */
export const updateLeadershipAssignment = async (id: number, data: Partial<CreateLeadershipAssignment>) => {
  try {
    const response = await httpClient.put(`/organization-setup/hierarchy/${id}`, data);

    return {
      success: response.success,
      data: response.data,
      error: response.error,
      message: response.message
    };
  } catch (error: any) {
    console.error('updateLeadershipAssignment error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to update assignment' }
    };
  }
};

/**
 * Remove leadership assignment
 */
export const removeLeadershipAssignment = async (id: number) => {
  try {
    const response = await httpClient.delete(`/organization-setup/hierarchy/${id}`);

    return {
      success: response.success,
      data: response.data,
      error: response.error,
      message: response.message
    };
  } catch (error: any) {
    console.error('removeLeadershipAssignment error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to remove assignment' }
    };
  }
};

// ============================================
// Stats & Employees API
// ============================================

/**
 * Get organization statistics
 */
export const getOrganizationStats = async () => {
  try {
    const response = await httpClient.get('/organization-setup/stats');

    return {
      success: response.success,
      data: response.data as OrganizationStats,
      error: response.error
    };
  } catch (error: any) {
    console.error('getOrganizationStats error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message }
    };
  }
};

/**
 * Get available employees for leadership assignment
 */
export const getAvailableEmployees = async () => {
  try {
    const response = await httpClient.get('/organization-setup/employees');

    return {
      success: response.success,
      data: (response.data || []) as AvailableEmployee[],
      error: response.error
    };
  } catch (error: any) {
    console.error('getAvailableEmployees error:', error);
    return {
      success: false,
      data: [],
      error: { message: error.message }
    };
  }
};

// ============================================
// Working Hours Settings API
// ============================================

/**
 * Get working hours settings
 */
export const getWorkingHoursSettings = async () => {
  try {
    const response = await httpClient.get('/organization-setup/working-hours');

    return {
      success: response.success,
      data: response.data as WorkingHoursSettings,
      error: response.error
    };
  } catch (error: any) {
    console.error('getWorkingHoursSettings error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message }
    };
  }
};

/**
 * Update working hours settings
 */
export const updateWorkingHoursSettings = async (data: Partial<WorkingHoursSettings>) => {
  try {
    const response = await httpClient.put('/organization-setup/working-hours', data);

    return {
      success: response.success,
      data: response.data as WorkingHoursSettings,
      error: response.error,
      message: response.message
    };
  } catch (error: any) {
    console.error('updateWorkingHoursSettings error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to update working hours settings' }
    };
  }
};

// ============================================
// Attendance Notification API
// ============================================

/**
 * Trigger attendance notifications manually
 */
export const triggerAttendanceNotifications = async () => {
  try {
    const response = await httpClient.post('/organization-setup/attendance/trigger-notifications');

    return {
      success: response.success,
      data: response.data as NotificationStatus,
      error: response.error,
      message: response.message
    };
  } catch (error: any) {
    console.error('triggerAttendanceNotifications error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to trigger attendance notifications' }
    };
  }
};

/**
 * Get notification status (last trigger time)
 */
export const getNotificationStatus = async () => {
  try {
    const response = await httpClient.get('/organization-setup/attendance/notification-status');

    return {
      success: response.success,
      data: response.data as NotificationStatus,
      error: response.error
    };
  } catch (error: any) {
    console.error('getNotificationStatus error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to fetch notification status' }
    };
  }
};

// ============================================
// Teams API
// ============================================

/**
 * Get all teams
 */
export const getAllTeams = async () => {
  try {
    const response = await httpClient.get('/organization-setup/teams');

    return {
      success: response.success,
      data: (response.data || []) as Team[],
      error: response.error
    };
  } catch (error: any) {
    console.error('getAllTeams error:', error);
    return {
      success: false,
      data: [],
      error: { message: error.message }
    };
  }
};

/**
 * Get a single team by ID
 */
export const getTeamById = async (id: number) => {
  try {
    const response = await httpClient.get(`/organization-setup/teams/${id}`);

    return {
      success: response.success,
      data: response.data as Team,
      error: response.error
    };
  } catch (error: any) {
    console.error('getTeamById error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message }
    };
  }
};

/**
 * Create a new team
 */
export const createTeam = async (data: CreateTeam) => {
  try {
    const response = await httpClient.post('/organization-setup/teams', data);

    return {
      success: response.success,
      data: response.data as Team,
      error: response.error,
      message: response.message
    };
  } catch (error: any) {
    console.error('createTeam error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create team' }
    };
  }
};

/**
 * Update a team
 */
export const updateTeam = async (id: number, data: Partial<CreateTeam>) => {
  try {
    const response = await httpClient.put(`/organization-setup/teams/${id}`, data);

    return {
      success: response.success,
      data: response.data as Team,
      error: response.error,
      message: response.message
    };
  } catch (error: any) {
    console.error('updateTeam error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to update team' }
    };
  }
};

/**
 * Delete a team
 */
export const deleteTeam = async (id: number) => {
  try {
    const response = await httpClient.delete(`/organization-setup/teams/${id}`);

    return {
      success: response.success,
      data: response.data,
      error: response.error,
      message: response.message
    };
  } catch (error: any) {
    console.error('deleteTeam error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to delete team' }
    };
  }
};

/**
 * Get available managers (not currently managing any team)
 */
export const getAvailableManagers = async () => {
  try {
    const response = await httpClient.get('/organization-setup/teams/available-managers');

    return {
      success: response.success,
      data: (response.data || []) as AvailableManager[],
      error: response.error
    };
  } catch (error: any) {
    console.error('getAvailableManagers error:', error);
    return {
      success: false,
      data: [],
      error: { message: error.message }
    };
  }
};

/**
 * Get all employees for manager dropdown
 */
export const getAllEmployeesForManager = async () => {
  try {
    const response = await httpClient.get('/organization-setup/teams/all-employees');

    return {
      success: response.success,
      data: (response.data || []) as AvailableManager[],
      error: response.error
    };
  } catch (error: any) {
    console.error('getAllEmployeesForManager error:', error);
    return {
      success: false,
      data: [],
      error: { message: error.message }
    };
  }
};
